import { Container, Row, Col, Button } from "react-bootstrap";
import { useState } from "react";
import { ApiKey, ApiKeysService } from "../../../services/apiKeysService";

interface ApiKeyListProps {
  apiKeys: ApiKey[];
  onApiKeyDeleted: (keyId: string) => void;
  onCreateKey?: () => void;
  isLoading?: boolean;
}

export default function ApiKeyList({ apiKeys, onApiKeyDeleted, onCreateKey, isLoading }: ApiKeyListProps) {
  const [deletingKeyId, setDeletingKeyId] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState<string | null>(null);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleDeleteKey = async (keyId: string) => {
    setDeletingKeyId(keyId);
    try {
      await ApiKeysService.deleteApiKey(keyId);
      onApiKeyDeleted(keyId);
      setShowConfirmDialog(null);
    } catch (error) {
      console.error("Failed to delete API key:", error);
      // You might want to show an error toast here
    } finally {
      setDeletingKeyId(null);
    }
  };

  if (isLoading) {
    return (
      <Container className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading API keys...</span>
        </div>
        <div className="mt-3 detail-page-subheading">Loading your API keys...</div>
      </Container>
    );
  }

  if (apiKeys.length === 0) {
    return (
      <Container className="text-center py-5">
        <Row className="justify-content-center">
          <Col md={8}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔑</div>
            <h4 className="detail-page-heading mb-3">No API Keys Yet</h4>
            <p className="detail-page-subheading mb-4">
              Create your first API key to start accessing the Windpuls API programmatically.
              API keys allow you to securely authenticate your requests and monitor usage.
            </p>
            <Button className="wind-btn" onClick={onCreateKey}>
              Create Your First API Key
            </Button>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid>
      {/* Delete Confirmation Modal */}
      {showConfirmDialog && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1050,
            backdropFilter: 'blur(4px)'
          }}
        >
          <Container
            className="background-color-light p-4 border-radius-4"
            style={{ maxWidth: '500px' }}
          >
            <h5 className="text-white mb-3">Confirm Deletion</h5>
            <p className="text-color mb-4">
              Are you sure you want to delete this API key? This action cannot be undone.
              Any applications using this key will immediately lose access.
            </p>
            <div className="d-flex gap-2 justify-content-end">
              <Button
                variant="outline-secondary"
                onClick={() => setShowConfirmDialog(null)}
                className="wind-btn-outline"
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => handleDeleteKey(showConfirmDialog)}
                disabled={deletingKeyId === showConfirmDialog}
                className="btn-red"
              >
                {deletingKeyId === showConfirmDialog ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Deleting...
                  </>
                ) : (
                  "Delete API Key"
                )}
              </Button>
            </div>
          </Container>
        </div>
      )}

      <Row className="mb-4">
        <Col>
          <h2 className="detail-page-heading mb-2">Your API Keys</h2>
          <p className="detail-page-subheading">
            Manage your API keys for programmatic access to the Windpuls API.
            Keep your keys secure and rotate them regularly.
          </p>
        </Col>
      </Row>

      {apiKeys.map((apiKey) => (
        <Container
          key={apiKey.key_id}
          className="p-3 mb-3 background-color border-radius-4"
          style={{ border: '1px solid #283654' }}
        >
          <Row className="align-items-center">
            <Col md={6}>
              <div className="d-flex align-items-center mb-2">
                <div
                  className={`status-indicator me-2 ${
                    apiKey.is_active ? 'circle-online-color' : 'circle-offline-color'
                  }`}
                />
                <h5 className="text-white mb-0">{apiKey.name}</h5>
              </div>
              <div className="font-monospace text-color mb-2" style={{ fontSize: '14px' }}>
                {ApiKeysService.maskApiKey(apiKey.api_key)}
              </div>
              <div className="d-flex gap-3 text-color" style={{ fontSize: '12px' }}>
                <span>Created: {formatDate(apiKey.created_at)}</span>
                <span>Last used: {formatDate(apiKey.last_used_at)}</span>
                <span>Usage: {apiKey.usage_count} requests</span>
              </div>
            </Col>
            <Col md={6} className="text-md-end mt-3 mt-md-0">
              <div className="d-flex gap-2 justify-content-md-end">
                <Button
                  variant="outline-danger"
                  size="sm"
                  className="btn-red-outline"
                  onClick={() => setShowConfirmDialog(apiKey.key_id)}
                  disabled={deletingKeyId !== null}
                >
                  {deletingKeyId === apiKey.key_id ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" />
                      Deleting...
                    </>
                  ) : (
                    "🗑️ Delete"
                  )}
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      ))}

      <Row className="mt-4">
        <Col>
          <Container className="p-3" style={{ backgroundColor: '#0ba4bf10', border: '1px solid #0ba4bf30', borderRadius: '8px' }}>
            <div className="d-flex align-items-start">
              <div className="me-3" style={{ color: '#0ba4bf', fontSize: '20px' }}>💡</div>
              <div>
                <div className="text-white mb-2" style={{ fontWeight: '600' }}>
                  Security Best Practices
                </div>
                <ul className="text-color mb-0" style={{ fontSize: '14px', paddingLeft: '20px' }}>
                  <li>Store your API keys securely and never commit them to version control</li>
                  <li>Rotate your keys regularly and delete unused keys</li>
                  <li>Monitor your usage and report any suspicious activity</li>
                  <li>Use environment variables in your applications</li>
                </ul>
              </div>
            </div>
          </Container>
        </Col>
      </Row>
    </Container>
  );
}